import {
  APP_IDENTITY_ROLES,
  type AppKeyRole,
  type AppObjectKind,
  SHARED_DOMAINS,
} from './types/app-objects.ts';
import type { UsageSheetAppData } from './types/forge-usage-sheet.ts';

/**
 * The keys of an object (L48), the same way the merge computes them in SQL
 * (`app_key_normalize_r1`, `app_row_keys_r1`): read by the publish gate and by
 * the step that copies role columns from the source.
 */

export interface AppObjectKey {
  role: AppKeyRole;
  value: string;
  /** Identifies the object for its kind; otherwise it only links it (its company, its domain). */
  strong: boolean;
}

const LEGAL_FORMS = new Set(['sas', 'sasu', 'sa', 'sarl', 'eurl', 'sci', 'snc', 'inc', 'ltd', 'llc', 'gmbh', 'bv', 'srl', 'spa', 'plc', 'ag']);
const SHARED = new Set<string>(SHARED_DOMAINS);

/** The words of a name or a text, without accents, apostrophes nor legal form. */
export const nameWords = (value: string): string[] => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/['’`´]/g, '').replace(/[^a-z0-9]+/g, ' ').trim().split(' ')
  .filter((word) => word && !LEGAL_FORMS.has(word));

/** « SOMM’IT SAS » and « Sommit » are the same name. */
export const normalizeObjectName = (value: string): string | null => {
  const words = nameWords(value);
  return words.length > 0 ? words.join(' ').slice(0, 500) : null;
};

/** A company's own domain, from a site, an address or a bare domain; never a shared one. */
export const normalizeCompanyDomain = (value: string): string | null => {
  const domain = value.trim().toLowerCase().replace(/^.*@/, '').replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/^www\./, '').replace(/[/?#:].*$/, '');
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain) && !SHARED.has(domain) ? domain : null;
};

const TRACKING = /^(utm_[a-z0-9_]+|fbclid|gclid|mc_[a-z]+|trk|ref|refid)$/;

export const normalizeObjectLink = (value: string): string | null => {
  const [base = '', query = ''] = value.trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/^www\./, '')
    .replace(/#.*$/, '').split('?');
  const kept = query.split('&').filter((pair) => pair && !TRACKING.test(pair.split('=')[0] ?? ''));
  const link = `${base.replace(/\/+$/, '')}${kept.length > 0 ? `?${kept.join('&')}` : ''}`;
  return link ? link.slice(0, 500) : null;
};

export const normalizeObjectKey = (role: AppKeyRole, raw: unknown): string | null => {
  const value = typeof raw === 'string' ? raw.trim() : typeof raw === 'number' ? String(raw) : '';
  if (!value) return null;
  if (role === 'domain') return normalizeCompanyDomain(value);
  if (role === 'url') return normalizeObjectLink(value);
  if (role === 'email') return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) ? value.toLowerCase().slice(0, 500) : null;
  if (role === 'name' || role === 'company') return normalizeObjectName(value);
  return value.slice(0, 500);
};

type Column = { key?: unknown; role?: unknown };
type Cell = { key?: unknown; value?: unknown };

/**
 * The keys of one result row. The first column names the element; a column
 * keyed exactly `domain` without a role is read as the domain (rows written by
 * a model step, F5f). In runs mode (`kind` null) no key identifies the object:
 * the launch does.
 */
export const objectKeysOfRow = (columns: unknown, cells: unknown, kind: AppObjectKind | null): AppObjectKey[] => {
  const cellList = (Array.isArray(cells) ? cells : []) as Cell[];
  const strongRoles: readonly string[] = kind ? APP_IDENTITY_ROLES[kind] : [];
  const keys: AppObjectKey[] = [];
  let name: string | null = null;
  let company: string | null = null;
  (Array.isArray(columns) ? columns as Column[] : []).forEach((column, index) => {
    const role = typeof column.role === 'string' ? column.role as AppKeyRole
      : index === 0 ? 'name' : column.key === 'domain' ? 'domain' : null;
    if (!role) return;
    const value = normalizeObjectKey(role, cellList.find((cell) => cell.key === column.key)?.value);
    if (!value) return;
    if (role === 'name') name = value;
    else {
      if (role === 'company') company = value;
      keys.push({ role, value, strong: strongRoles.includes(role) });
    }
  });
  if (name) keys.push({ role: 'name', value: (kind === 'person' || kind === 'posting') && company ? `${name} @ ${company}` : name, strong: false });
  return keys;
};

const LEGACY_COMPANY = /entreprise|soci[eé]t[eé]|company|compte|account|domain|domaine|site/i;

/**
 * Sheets saved before L48 named an identity column; they become the kind it
 * pointed to (a company, else something else). Applied wherever a stored sheet
 * is read back.
 */
export const upgradeLegacyAppData = (raw: unknown): unknown => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw) || 'object' in raw) return raw;
  const { identity, ...rest } = raw as Record<string, unknown> & { identity?: { key?: unknown; label?: unknown } | null };
  if (rest.mode !== 'rows') return { ...rest, object: null };
  const words = identity ? `${String(identity.key ?? '')} ${String(identity.label ?? '')}` : '';
  return { ...rest, object: LEGACY_COMPANY.test(words) ? 'company' : 'other' } satisfies Partial<UsageSheetAppData>;
};

/**
 * Why the columns of a judged table cannot carry their roles (L48), in words
 * for the assembly; null when they can. The first column names the element.
 */
export const judgeColumnsProblem = (columns: ReadonlyArray<{ key: string; label: string; role?: string; from?: string }>): string | null => {
  if (columns[0]?.role) return `La première colonne (« ${columns[0].label} ») nomme l’élément : elle ne porte pas de rôle.`;
  const roled = columns.filter((column) => column.role);
  const withoutSource = roled.find((column) => !column.from?.trim());
  if (withoutSource) return `La colonne « ${withoutSource.label} » porte un rôle : dites de quel champ des éléments le code la recopie (from).`;
  const roles = roled.map((column) => column.role);
  const repeated = roles.find((role, index) => roles.indexOf(role) !== index);
  return repeated ? `Deux colonnes portent le rôle « ${repeated} » : une seule colonne par rôle.` : null;
};
